//@ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Tooltip,
  message,
} from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  CheckOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";
import CommonHeader from "../../Components/commonHeader";
import QuoteIdQuickNav from "../../Components/QuoteIdQuickNav";
import { normalizeSortRules, sortRulesToQuery, type SortRule } from "../../utils/sortRules";
import {
  getShippedOrdersByDateRange,
  getShippedOrderMonthRange,
  getOtcBreakEvenByMonth,
  upsertOtcBreakEvenByMonth,
  updateOrderTracking,
  getLeadFromOptions,
  bulkImportOTC,
  getOrderTrackingByQuoteId,
  updateOpenOrderComments,
  getShipTos,
  getCalTypes,
  getManufacturers,
  getVendors,
} from "../../utils/API";
import {
  getStatuses,
  createStatus,
  updateStatus,
  deleteStatus,
} from "../../utils/API";

const OTC_EXPORT_COLUMNS = [
  { label: "Quote ID", key: "quote_id" },
  { label: "Date", key: "date" },
  { label: "Customer", key: "customer" },
  { label: "GM", key: "gm" },
  { label: "GS", key: "gs" },
  { label: "Status", key: "status" },
  { label: "Model", key: "model" },
  { label: "Comments", key: "comment" },
  { label: "PO Sent", key: "poSent" },
  { label: "Paid", key: "paid" },
  { label: "Due Date", key: "dueDate" },
  { label: "Lead From", key: "leadFrom" },
];

const OTC_SORT_KEYS = OTC_EXPORT_COLUMNS.map((column) => column.key);

// An order can carry up to five model lines. The grid row stays collapsed to a
// single line (slot 1) the way it always has; expanding a row reveals every
// slot that actually has a model on it. Shared with the invoice-request email
// builder so both views agree on what "the model lines" means.
// Only lines 1 and 2 have date_shipped / rental_period columns in the DB, so
// `ship` and `rentalPeriod` are absent on lines 3-5 rather than empty. Order
// Tracking Center draws the same distinction with its showShip/showRental flags.
const OTC_MODEL_SLOTS = [
  {
    manu: "manu",
    model: "model",
    sn: "sn",
    qty: "qty",
    vendor: "vendor1",
    ship: "dateShipped",
    rentalPeriod: "rentalPeriod",
    tracking: "trackingNo",
  },
  {
    manu: "manu2",
    model: "model2",
    sn: "sn2",
    qty: "qty2",
    vendor: "vendor2",
    ship: "dateShipped2",
    rentalPeriod: "rentalPeriod2",
    tracking: "trackingNo2",
  },
  {
    manu: "manu3",
    model: "model3",
    sn: "sn3",
    qty: "qty3",
    vendor: "vendor3",
    tracking: "trackingNo3",
  },
  {
    manu: "manu4",
    model: "model4",
    sn: "sn4",
    qty: "qty4",
    vendor: "vendor4",
    tracking: "trackingNo4",
  },
  {
    manu: "manu5",
    model: "model5",
    sn: "sn5",
    qty: "qty5",
    vendor: "vendor5",
    tracking: "trackingNo5",
  },
];

// A slot field can be absent for this line (no such DB column) or present but
// empty. `null` means "not applicable", "" means "blank" — the row renders them
// differently so an empty cell never reads as missing data.
const slotValue = (record: any, field?: string) =>
  field ? String(record?.[field] ?? "").trim() : null;

const getModelSlots = (record: any) =>
  OTC_MODEL_SLOTS.map((slot, index) => ({
    line: index + 1,
    // The row-field names behind this line, so an editable cell can report
    // which one it changed.
    fields: slot,
    manu: slotValue(record, slot.manu) || "",
    model: slotValue(record, slot.model) || "",
    sn: slotValue(record, slot.sn) || "",
    qty: slotValue(record, slot.qty) || "",
    vendor: slotValue(record, slot.vendor) || "",
    ship: slotValue(record, slot.ship),
    rentalPeriod: slotValue(record, slot.rentalPeriod),
    tracking: slotValue(record, slot.tracking) || "",
  })).filter((slot) => slot.model);

const inputStyle =
  "w-full h-7 !text-sm rounded border border-[#A29F9F] px-2 custom-input-size";
const labelStyle = "text-[14px] text-[#6C757D] mb-1 font-medium";
const navBtnBaseClass =
  "!h-6 !px-3 !py-0 !text-[12px] !leading-none !rounded-none !border !border-[#5f5f5f] !shadow-none";

// Sidebar input
const SidebarInputBox = ({ label }: any) => (
  <div className="flex flex-col mb-3">
    <label className={labelStyle}>{label}</label>
    <Input className={inputStyle} size="small" />
  </div>
);

interface StatusOption {
  id: number;
  value: string;
  label: string;
  type: string;
}

const WIN_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatOtcCurrencyValue = (value: any) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return `$${Math.round(numericValue).toLocaleString()}`;
};

const parseOtcCurrencyValue = (value: any) => {
  const normalized = String(value || "").replace(/[$,]/g, "");
  return Number.parseFloat(normalized) || 0;
};

const WinDatePicker = ({
  value,
  onChange,
  label,
}: {
  value: dayjs.Dayjs;
  onChange: (d: dayjs.Dayjs) => void;
  label?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(value || dayjs());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) setViewing(value);
  }, [value?.format("YYYY-MM")]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const startOfMonth = viewing.startOf("month");
  const startDay = startOfMonth.day();
  const firstCell = startOfMonth.subtract(startDay, "day");
  const cells = Array.from({ length: 42 }, (_, i) => firstCell.add(i, "day"));
  const today = dayjs();

  return (
    <div ref={containerRef} className="relative" style={{ userSelect: "none" }}>
      {label && <div className="font-semibold mb-0.5 text-[12px]">{label}</div>}
      {/* Input trigger */}
      <div
        className="flex items-center gap-1 cursor-pointer border border-gray-400 bg-white px-2 h-6 text-[11px] rounded-sm"
        style={{ minWidth: 110 }}
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarOutlined className="text-gray-500 text-[10px]" />
        <span>{value ? value.format("MM/DD/YYYY") : "—"}</span>
      </div>

      {/* Calendar popup */}
      {open && (
        <div
          className="absolute z-50 mt-0.5"
          style={{
            background: "#f0f0f0",
            border: "1px solid #aaa",
            boxShadow: "2px 3px 8px rgba(0,0,0,0.25)",
            width: 210,
            padding: "4px",
          }}
        >
          {/* Month/Year header */}
          <div
            className="flex items-center justify-between"
            style={{ padding: "2px 0 4px 0" }}
          >
            <span
              className="cursor-pointer font-bold text-[12px] text-gray-700 border border-gray-400 bg-white px-1.5 leading-none"
              style={{ lineHeight: "16px" }}
              onClick={() => setViewing((v) => v.subtract(1, "month"))}
            >
              {"‹"}
            </span>
            <span className="text-[12px] font-bold text-gray-800">
              {viewing.format("MMMM YYYY")}
            </span>
            <span
              className="cursor-pointer font-bold text-[12px] text-gray-700 border border-gray-400 bg-white px-1.5 leading-none"
              style={{ lineHeight: "16px" }}
              onClick={() => setViewing((v) => v.add(1, "month"))}
            >
              {"›"}
            </span>
          </div>

          {/* Day name headers */}
          <div
            className="grid"
            style={{ gridTemplateColumns: "repeat(7, 1fr)" }}
          >
            {WIN_DAYS.map((d, idx) => (
              <div
                key={d}
                className="text-center text-[10px] font-semibold py-[3px]"
                style={{ color: "#333" }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Date grid */}
          <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
            {cells.map((cell, i) => {
              const isCurrentMonth = cell.month() === viewing.month();
              const isSelected = value && cell.isSame(value, "day");
              const isToday = cell.isSame(today, "day");
              const isSunday = cell.day() === 0;
              const baseColor = isCurrentMonth ? "#111" : "#b8c8d8";
              return (
                <div
                  key={i}
                  className="text-center text-[11px] cursor-pointer"
                  style={{
                    padding: "2px 0",
                    color: isSelected ? "#fff" : baseColor,
                    background: isSelected ? "#555" : "transparent",
                    borderBottom: isToday && !isSelected ? "2px solid #555" : "2px solid transparent",
                    fontWeight: isSelected || isToday ? 700 : 400,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = "#e8e8e8";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = "#f0f0f0";
                  }}
                  onClick={() => { onChange(cell); setOpen(false); }}
                >
                  {cell.date()}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Formats a DATEONLY coming back from the API. Values arrive as YYYY-MM-DD and
// must not be run through a timezone conversion, or a shipped date can slip a day.
const formatSnapshotDate = (value: any) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  // Reformat the date part by hand. Passing it through dayjs() would treat a
  // bare YYYY-MM-DD as UTC and shift it a day in negative-offset timezones,
  // and strict parsing is unavailable here (customParseFormat is not loaded).
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : raw;
};

const EM_DASH = "—";

// order_tracking columns the Snapshot may write, keyed by the row's field name.
// updateOrderTracking's buildPayload only writes keys that are present, so a
// single-field patch here is safe.
const OT_FIELD_MAP: Record<string, string> = {
  model: "model",
  comment: "otc_comments",
  poSent: "vendor",
  paid: "paid",
  dueDate: "invoice_due_date",
  leadFrom: "lead_from",

  // Detail fields shown in the expanded row.
  salesOrder: "sales_order",
  custPo: "po_number",
  rma: "rma",
  rmaComment: "rma_comment",
  vendorRma: "vendor_rma",
  orderComplete: "order_complete",
  vendorPaid: "when",
  invoiceDate: "invoice_date",
  ateInvoiceNo: "ate_invoice_no",
  invoiceTotal: "invoice_total",
  actions: "actions",

  // Model lines. Note the inconsistent column naming this has to absorb:
  // model_2 but manu2, serial_num1 but tracking_no, qty_2 but vendor2.
  manu: "manu",
  model: "model",
  sn: "serial_num1",
  qty: "qty",
  vendor1: "vendor",
  trackingNo: "tracking_no",
  dateShipped: "date_shipped",
  rentalPeriod: "rental_period",
  manu2: "manu2",
  model2: "model_2",
  sn2: "serial_num2",
  qty2: "qty_2",
  vendor2: "vendor2",
  trackingNo2: "tracking_no_2",
  dateShipped2: "date_shipped_2",
  rentalPeriod2: "rental_period2",
  manu3: "manu3",
  model3: "model_3",
  sn3: "serial_num3",
  qty3: "qty_3",
  vendor3: "vendor3",
  trackingNo3: "tracking_no_3",
  manu4: "manu4",
  model4: "model_4",
  sn4: "serial_num4",
  qty4: "qty_4",
  vendor4: "vendor4",
  trackingNo4: "tracking_no_4",
  manu5: "manu5",
  model5: "model_5",
  sn5: "serial_num5",
  qty5: "qty_5",
  vendor5: "vendor5",
  trackingNo5: "tracking_no_5",
};

// Detail fields that hold a DATEONLY, so the editor offers a date input and the
// value round-trips as YYYY-MM-DD.
const OT_DATE_FIELDS = new Set([
  "vendorPaid",
  "invoiceDate",
  "dueDate",
  "dateShipped",
  "dateShipped2",
]);

// Quantity columns, so the cell can be right-sized and typed as numeric.
const OT_QTY_FIELDS = new Set(["qty", "qty2", "qty3", "qty4", "qty5"]);

// Fields that are dropdowns on their own page, and where the choices come from.
// Keeping the same control here means a value typed in the Snapshot cannot
// drift from the vocabulary the OOR / Order Tracking pages enforce.
//
// The status-type strings are the ones those pages filter getStatuses() by.
const OOR_STATUS_TYPES = {
  status: "open_order",
  priority: "openorder_priority",
  stock: "openorder_stock",
  action: "openorder_ss", // OOR labels this Sub-Status
  resolution: "openorder_resolution",
};

// Expedite is a fixed two-value list on the OOR page, not a managed status.
const EXPEDITE_OPTIONS = [
  { value: "YES SEE NOTES !", label: "YES SEE NOTES !" },
  { value: "NO", label: "NO" },
];

// Order Tracking Center offers exactly these two.
const ORDER_COMPLETE_OPTIONS = [
  { value: "Active order", label: "Active order" },
  { value: "Closed", label: "Closed" },
];

const STATUS_TYPE_OTC_ACTIONS = "otc-actions";

const OOR_DATE_FIELDS = new Set([
  "date_created",
  "po_date",
  "po_sent",
  "po_sent2",
  "vendor_shipped",
  "vendor_shipped2",
  "vendor_shipped3",
  "wire_sent",
  "wire_sent2",
  "wire_sent3",
]);

// One label/value pair inside a detail section.
// One label/value pair. Editable when an onSave is supplied, read-only otherwise.
//
// The draft is local while typing and only pushed up on blur. It re-syncs from
// the prop ONLY while the input is unfocused: the parent re-renders on every
// optimistic patch, and copying the prop into the draft mid-edit would yank
// characters out from under the cursor. That focus guard is what keeps typing
// smooth while other rows are saving.
const DetailField = ({ label, value, onSave, type = "text" }: any) => {
  const [draft, setDraft] = useState(String(value ?? ""));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value ?? ""));
  }, [value, focused]);

  if (!onSave) {
    return (
      <div className="otc-detail-field">
        <span className="otc-detail-label">{label}</span>
        <span className="otc-detail-value">{value || EM_DASH}</span>
      </div>
    );
  }

  const commit = () => {
    setFocused(false);
    const next = draft.trim();
    if (next === String(value ?? "").trim()) return;
    onSave(next);
  };

  return (
    <div className="otc-detail-field is-editable">
      <span className="otc-detail-label">{label}</span>
      <input
        type={type}
        className="otc-detail-input"
        // Lets the stylesheet keep an empty date mask muted while showing a
        // real date in normal text.
        data-filled={draft ? "1" : "0"}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          // Escape abandons the edit and restores what the record holds.
          if (e.key === "Escape") {
            setDraft(String(value ?? ""));
            setFocused(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={EM_DASH}
      />
    </div>
  );
};

// A dropdown detail field. Commits on change rather than on blur — picking an
// option is already a deliberate act, so there is nothing to defer. Styled to
// match the text fields so a row of mixed controls still lines up.
const SelectField = ({ label, value, options, onSave }: any) => {
  const current = String(value ?? "");
  // Options can legitimately be missing: the lists load asynchronously, and a
  // failed fetch leaves one empty. Never assume an array here.
  const safe = Array.isArray(options) ? options : [];
  // A record can hold a value that has since been removed from the managed
  // list. Showing it as an extra option keeps it visible instead of silently
  // rendering the field blank.
  const known = safe.some((o: any) => o.value === current);
  const list = !current || known ? safe : [...safe, { value: current, label: current }];

  return (
    <div className="otc-detail-field is-editable">
      <span className="otc-detail-label">{label}</span>
      <select
        className="otc-detail-select"
        value={current}
        onChange={(e) => onSave?.(e.target.value)}
      >
        <option value="">{EM_DASH}</option>
        {list.map((o: any) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Dropdown variant of ItemInput, for item columns that are managed lists on the
// OOR page. Commits on change; an unknown stored value is kept as an option so
// the cell never silently blanks.
const ItemSelect = ({ value, options, onSave }: any) => {
  const current = String(value ?? "");
  const safe = Array.isArray(options) ? options : [];
  const known = safe.some((o: any) => o.value === current);
  const list = !current || known ? safe : [...safe, { value: current, label: current }];

  return (
    <select
      className="otc-item-select"
      value={current}
      onChange={(e) => onSave?.(e.target.value)}
    >
      <option value="">{EM_DASH}</option>
      {list.map((o: any) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
};

// A single item cell. Same contract as DetailField: local draft while typing,
// re-synced from the prop only when unfocused, committed on blur, saved
// optimistically by the parent.
const ItemInput = ({ value, onSave, numeric, type = "text" }: any) => {
  const [draft, setDraft] = useState(String(value ?? ""));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value ?? ""));
  }, [value, focused]);

  const commit = () => {
    setFocused(false);
    const next = draft.trim();
    if (next === String(value ?? "").trim()) return;
    onSave?.(next);
  };

  return (
    <input
      type={type}
      inputMode={numeric ? "numeric" : undefined}
      className={`otc-item-input${numeric ? " is-numeric" : ""}${
        type === "date" ? " is-date" : ""
      }`}
      data-filled={draft ? "1" : "0"}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") {
          setDraft(String(value ?? ""));
          setFocused(false);
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder={EM_DASH}
    />
  );
};

// OOR stores three ORDER BLOCKS per record, not three comments per topic. Each
// block is a separate vendor/PO/shipment and carries its own vendor, PO-sent,
// shipped date, tracking, ship-to, cal type and four comments -- the suffix is
// the block number (see the orderBlocks mapping in open-order.tsx).
//
// So general_comments2 is "the general comment for block 2", NOT "the second
// general comment". Grouping by block keeps each note attached to the shipment
// it describes; grouping by topic would put three different vendors' notes in
// one pile.
const OOR_COMMENT_BLOCKS = [
  {
    block: 1,
    vendorField: "vendor",
    items: [
      {
        manu: "manu",
        model: "model",
        qty: "qty",
        serial: "serial_number1",
        opt: "options",
      },
      {
        manu: "manu2",
        model: "model2",
        qty: "qty2",
        serial: "serial_number2",
        opt: "opt2",
      },
      {
        manu: "manu3",
        model: "model3",
        qty: "qty3",
        serial: "serial_number3",
        opt: "opt3",
      },
    ],
    trackingField: "tracking_number",
    fields: [
      { label: "General", key: "general_comments" },
      { label: "Vendor", key: "vendor_comments" },
      { label: "Cal Lab", key: "cal_lab_comments" },
      { label: "RMA", key: "rma_notes" },
    ],
  },
  {
    block: 2,
    vendorField: "vendor2",
    items: [
      {
        manu: "manu4",
        model: "model4",
        qty: "qty4",
        serial: "serial_number4",
        opt: "opt4",
      },
      {
        manu: "manu5",
        model: "model5",
        qty: "qty5",
        serial: "serial_number5",
        opt: "opt5",
      },
      {
        manu: "manu6",
        model: "model6",
        qty: "qty6",
        serial: "serial_number6",
        opt: "opt6",
      },
    ],
    trackingField: "tracking_number2",
    fields: [
      { label: "General", key: "general_comments2" },
      { label: "Vendor", key: "vendor_comments2" },
      { label: "Cal Lab", key: "cal_lab_comments2" },
      { label: "RMA", key: "rma_notes2" },
    ],
  },
  {
    block: 3,
    vendorField: "vendor3",
    items: [
      {
        manu: "manu7",
        model: "model7",
        qty: "qty7",
        serial: "serial_number7",
        opt: "opt7",
      },
      {
        manu: "manu8",
        model: "model8",
        qty: "qty8",
        serial: "serial_number8",
        opt: "opt8",
      },
      {
        manu: "manu9",
        model: "model9",
        qty: "qty9",
        serial: "serial_number9",
        opt: "opt9",
      },
    ],
    trackingField: "tracking_number3",
    fields: [
      { label: "General", key: "general_comments3" },
      { label: "Vendor", key: "vendor_comments3" },
      { label: "Cal Lab", key: "cal_lab_comments3" },
      { label: "RMA", key: "rma_notes3" },
    ],
  },
];

// A block is worth showing if it has any comment or any vendor/tracking of its
// own. Most orders ship from one vendor, so blocks 2 and 3 usually collapse
// away -- that is where the height saving comes from.
// A block counts as used if anything on it is filled in: a comment, a line
// item, or its own vendor/tracking. Items matter as much as comments here --
// a block can carry three models and no notes at all.
//
// Quantity is the exception: the column defaults to 0, so every unused block
// arrives with three zero-qty rows. A qty only counts as content when it is
// greater than zero; the text slots still count on being non-empty.
const itemHasContent = (oor: any, item: any) =>
  ["manu", "model", "serial", "opt"].some(
    (slot) => String(oor?.[item[slot]] ?? "").trim() !== "",
  ) || Number(oor?.[item.qty] ?? 0) > 0;

const blockHasContent = (oor: any, block: any) =>
  block.fields.some((f: any) => String(oor?.[f.key] ?? "").trim()) ||
  block.items.some((item: any) => itemHasContent(oor, item)) ||
  String(oor?.[block.vendorField] ?? "").trim() !== "" ||
  String(oor?.[block.trackingField] ?? "").trim() !== "";

// An editable comment field. Kept as local state while typing and committed on
// blur, so a slow save cannot fight the keystrokes. The prop resets the draft
// whenever the underlying record changes (month switch, refetch).
// An editable comment. Same contract as DetailField: local draft while typing,
// re-synced from the prop only when unfocused, committed on blur. The save is
// fire-and-forget from here — the parent patches state immediately and reverts
// on failure, so there is nothing to spin on.
// Minimum size the editor opens to. A comment cell is only about three table
// rows tall, which is fine for reading and cramped for writing.
const COMMENT_EDITOR_MIN_WIDTH = 380;
const COMMENT_EDITOR_MIN_HEIGHT = 200;

const CommentBlock = ({ value, onSave }: any) => {
  const [draft, setDraft] = useState(String(value ?? ""));
  const [focused, setFocused] = useState(false);
  // Where the expanded editor sits on screen. The editor is fixed-positioned
  // rather than grown in place: the table wrapper scrolls horizontally, so
  // anything overflowing its cell would be clipped, and growing the cell would
  // reflow every row below it.
  const [anchor, setAnchor] = useState<any>(null);
  // Stays in the cell and in normal flow even while the textarea is lifted out,
  // so there is always something to measure the editor's position against.
  const holderRef = useRef<HTMLSpanElement | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!focused) setDraft(String(value ?? ""));
  }, [value, focused]);

  const measure = () => {
    const rect = holderRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Grow past the cell, then keep the box inside the viewport when the cell
    // sits near an edge.
    const width = Math.max(rect.width, COMMENT_EDITOR_MIN_WIDTH);
    const height = Math.max(rect.height, COMMENT_EDITOR_MIN_HEIGHT);
    setAnchor({
      top: Math.max(8, Math.min(rect.top, window.innerHeight - height - 8)),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      width,
      height,
    });
  };

  // A fixed-position box does not move with the page, so re-anchor it to the
  // cell as things scroll instead of closing the editor. Scrolling *inside* the
  // editor is ignored — that is the user reading their own comment, and it used
  // to collapse the box mid-read.
  useEffect(() => {
    if (!focused) return;

    let frame = 0;
    const reposition = (event?: Event) => {
      if (event && event.target === ref.current) return;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [focused]);

  const open = () => {
    measure();
    setFocused(true);
  };

  const commit = () => {
    setFocused(false);
    setAnchor(null);
    const next = draft.trim();
    if (next === String(value ?? "").trim()) return;
    onSave?.(next);
  };

  return (
    <span className="otc-comment-holder" ref={holderRef}>
      <textarea
        ref={ref}
        className={`otc-comment-input${focused ? " is-editing" : ""}`}
        style={
          focused && anchor
            ? {
                position: "fixed",
                top: anchor.top,
                left: anchor.left,
                width: anchor.width,
                height: anchor.height,
              }
            : undefined
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={open}
        onBlur={commit}
        onKeyDown={(e) => {
          // Escape abandons the edit; the effect above re-syncs from the record.
          if (e.key === "Escape") {
            setDraft(String(value ?? ""));
            setFocused(false);
            setAnchor(null);
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        placeholder={EM_DASH}
      />
    </span>
  );
};

// One order block: the General note first, full width, then its three line
// items with the block's remaining comments as columns after Options.
//
// General carries the running shipment log (delivery dates, transit scans), so
// it is the thing being read on every visit -- as a ~130px table column it was
// a three-line scrolling slit. It gets the full pane width above the table and
// everything else stays below. Vendor/Cal Lab/RMA are short and stay columns,
// where each comment cell still spans all three item rows: a comment belongs to
// the whole block, not to any one item.
const BlockItems = ({ block, oor, onSave, manuOptions }: any) => {
  // Match on the key, not the label: the keys are block-suffixed
  // (general_comments2) but always start with the topic.
  const generalField = block.fields.find((f: any) =>
    String(f.key).startsWith("general_comments"),
  );
  const columnFields = block.fields.filter((f: any) => f !== generalField);

  return (
  <div className="otc-item-wrap">
    {generalField ? (
      <div className="otc-block-general">
        <div className="otc-block-general-label">{generalField.label}</div>
        <CommentBlock
          value={oor?.[generalField.key]}
          onSave={(next: string) => onSave?.(generalField.key, next)}
        />
      </div>
    ) : null}
    <table className="otc-item-table">
      <thead>
        <tr>
          <th style={{ width: 24 }}>#</th>
          <th style={{ width: 118 }}>Manufacturer</th>
          <th style={{ width: 110 }}>Model</th>
          <th style={{ width: 42 }}>Qty</th>
          <th style={{ width: 112 }}>Serial Number</th>
          <th style={{ width: 104 }}>Options</th>
          {columnFields.map((field: any) => (
            <th key={field.key} className="otc-item-comment-head">
              {field.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.items.map((item: any, index: number) => (
          <tr key={item.model}>
            <td className="otc-item-num">{index + 1}</td>
            {["manu", "model", "qty", "serial", "opt"].map((slot) =>
              // Manufacturer is a managed list on the OOR page, so it stays a
              // dropdown here; the rest are free text there and here.
              slot === "manu" ? (
                <td key={slot}>
                  <ItemSelect
                    value={oor?.[item[slot]]}
                    options={manuOptions}
                    onSave={(next: string) => onSave?.(item[slot], next)}
                  />
                </td>
              ) : (
                <td key={slot}>
                  <ItemInput
                    value={oor?.[item[slot]]}
                    numeric={slot === "qty"}
                    onSave={(next: string) => onSave?.(item[slot], next)}
                  />
                </td>
              ),
            )}
            {/* Comment cells are emitted once, on the first row, and span the
                rest — hence the index guard. */}
            {index === 0
              ? columnFields.map((field: any) => (
                  <td
                    key={field.key}
                    rowSpan={block.items.length}
                    className="otc-item-comment-cell"
                  >
                    <CommentBlock
                      value={oor?.[field.key]}
                      onSave={(next: string) => onSave?.(field.key, next)}
                    />
                  </td>
                ))
              : null}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

// One order block's comments: the four topics laid out side by side, headed by
// the vendor that block ships from so the notes are attributable at a glance.
const CommentBlockCard = ({ block, oor, onSave, manuOptions }: any) => {
  const [open, setOpen] = useState(true);
  const vendor = String(oor?.[block.vendorField] ?? "").trim();

  return (
    <div className="otc-comment-block-card">
      <button
        type="button"
        className="otc-comment-block-head"
        aria-expanded={open}
        onClick={() => setOpen((prev: boolean) => !prev)}
      >
        <CaretRightOutlined
          className={`otc-detail-caret${open ? " is-open" : ""}`}
        />
        <span className="otc-comment-block-title">Block {block.block}</span>
        {vendor ? (
          <span className="otc-comment-block-vendor">{vendor}</span>
        ) : null}
      </button>
      {open ? <BlockItems
            block={block}
            oor={oor}
            onSave={onSave}
            manuOptions={manuOptions}
          /> : null}
    </div>
  );
};

// A collapsible section inside the expanded row. Both the Order Tracking and
// Open Order halves use this so they read as siblings rather than one being
// nested inside the other.
const DetailSection = ({ title, subtitle, children, defaultOpen = true }: any) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="otc-detail-section">
      <button
        type="button"
        className="otc-detail-heading"
        aria-expanded={open}
        onClick={() => setOpen((prev: boolean) => !prev)}
      >
        <CaretRightOutlined
          className={`otc-detail-caret${open ? " is-open" : ""}`}
        />
        <span>{title}</span>
        {subtitle ? <span className="otc-detail-subtitle">{subtitle}</span> : null}
      </button>
      {open ? <div className="otc-detail-body">{children}</div> : null}
    </div>
  );
};

// The expanded half of a Snapshot row. Two sibling sections: the order_tracking
// record this grid is built from, and the open_order (OOR) record joined to it
// on quote_id. They are separate tables sharing only ~14 columns, so they are
// labelled separately and never blended into one list.
const OtcExpandedRow = ({
  record,
  options,
  leadFromOptions,
  onSaveOt,
  onSaveOor,
}: any) => {
  const slots = getModelSlots(record);
  const oor = record?.openOrder || null;

  // Maps a table cell back to the row field behind it. The slot descriptor
  // carries the per-line column names, so line 3's "model" resolves to model3.
  const saveSlot = (slot: any, key: string, next: string) => {
    const field = slot.fields?.[key];
    if (field) onSaveOt?.(record, field, next);
  };

  const rentalLabel =
    record?.rental === true ? "Yes" : record?.rental === false ? "No" : "";

  // order_tracking field: date inputs need the raw YYYY-MM-DD, everything else
  // shows whatever the row holds.
  const otProps = (field: string) => ({
    value: OT_DATE_FIELDS.has(field)
      ? String(record?.[field] ?? "").slice(0, 10)
      : record?.[field],
    type: OT_DATE_FIELDS.has(field) ? "date" : "text",
    onSave: (next: string) => onSaveOt?.(record, field, next),
  });

  // Dropdown equivalents of otProps / oorProps.
  const otSelect = (field: string, list: any[]) => ({
    value: record?.[field],
    options: list || [],
    onSave: (next: string) => onSaveOt?.(record, field, next),
  });

  const oorSelect = (field: string) => ({
    value: oor?.[field],
    options: options?.[field] || [],
    onSave: (next: string) => onSaveOor?.(record, field, next),
  });

  // Only blocks with something on them are drawn — an empty block 2 or 3 is
  // hidden outright rather than left as a collapsed stub. If the record is
  // completely empty, block 1 is still shown so there is somewhere to type.
  const usedBlocks = OOR_COMMENT_BLOCKS.filter((block) =>
    blockHasContent(oor, block),
  );
  const visibleBlocks =
    usedBlocks.length > 0 ? usedBlocks : OOR_COMMENT_BLOCKS.slice(0, 1);

  const oorProps = (field: string) => ({
    value: OOR_DATE_FIELDS.has(field)
      ? String(oor?.[field] ?? "").slice(0, 10)
      : oor?.[field],
    type: OOR_DATE_FIELDS.has(field) ? "date" : "text",
    onSave: (next: string) => onSaveOor?.(record, field, next),
  });

  return (
    <div className="otc-expanded-panel">
      <div className="otc-expanded-scroll">
        {/* ---------- Order Tracking ---------- */}
        <DetailSection title="Order Tracking Detail">
          {slots.length === 0 ? (
            <div className="otc-item-empty">
              No model lines on this order.
            </div>
          ) : (
          <div className="otc-item-wrap">
            <table className="otc-item-table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}>#</th>
                  <th style={{ width: 120 }}>Manufacturer</th>
                  <th style={{ width: 130 }}>Model</th>
                  <th style={{ width: 120 }}>Serial #</th>
                  <th style={{ width: 42 }}>Qty</th>
                  <th style={{ width: 120 }}>Vendor</th>
                  <th style={{ width: 104 }}>Shipped</th>
                  <th style={{ width: 96 }}>Rental Period</th>
                  <th>Tracking #</th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot) => (
                  <tr key={slot.line}>
                    <td className="otc-item-num">{slot.line}</td>
                    <td>
                      <ItemSelect
                        value={slot.manu}
                        options={options?.manu}
                        onSave={(next: string) => saveSlot(slot, "manu", next)}
                      />
                    </td>
                    <td>
                      <ItemInput
                        value={slot.model}
                        onSave={(next: string) => saveSlot(slot, "model", next)}
                      />
                    </td>
                    <td>
                      <ItemInput
                        value={slot.sn}
                        onSave={(next: string) => saveSlot(slot, "sn", next)}
                      />
                    </td>
                    <td>
                      <ItemInput
                        value={slot.qty}
                        numeric
                        onSave={(next: string) => saveSlot(slot, "qty", next)}
                      />
                    </td>
                    <td>
                      <ItemSelect
                        value={slot.vendor}
                        options={options?.vendor}
                        onSave={(next: string) => saveSlot(slot, "vendor", next)}
                      />
                    </td>
                    {/* Lines 3-5 have no shipped-date or rental-period column
                        in the table, so those cells are inert rather than
                        editable — see the OTC_MODEL_SLOTS descriptor. */}
                    <td>
                      {slot.ship === null ? (
                        <span className="otc-item-na">n/a</span>
                      ) : (
                        <ItemInput
                          value={String(slot.ship || "").slice(0, 10)}
                          type="date"
                          onSave={(next: string) => saveSlot(slot, "ship", next)}
                        />
                      )}
                    </td>
                    <td>
                      {slot.rentalPeriod === null ? (
                        <span className="otc-item-na">n/a</span>
                      ) : (
                        <ItemInput
                          value={slot.rentalPeriod}
                          onSave={(next: string) =>
                            saveSlot(slot, "rentalPeriod", next)
                          }
                        />
                      )}
                    </td>
                    <td>
                      <ItemInput
                        value={slot.tracking}
                        onSave={(next: string) => saveSlot(slot, "tracking", next)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <div className="otc-detail-grid">
            <DetailField label="Sales Order" {...otProps("salesOrder")} />
            <DetailField label="Cust PO" {...otProps("custPo")} />
            <DetailField label="RMA" {...otProps("rma")} />
            <DetailField label="Vendor RMA" {...otProps("vendorRma")} />
            <DetailField label="Rental" value={rentalLabel} />
            <SelectField
              label="Order Complete"
              {...otSelect("orderComplete", ORDER_COMPLETE_OPTIONS)}
            />
            <DetailField label="Vendor Paid" {...otProps("vendorPaid")} />
            <DetailField label="Invoice Date" {...otProps("invoiceDate")} />
            <DetailField label="Invoice #" {...otProps("ateInvoiceNo")} />
            <DetailField label="Invoice Total" {...otProps("invoiceTotal")} />
            <SelectField
              label="Lead From"
              {...otSelect("leadFrom", leadFromOptions)}
            />
            <SelectField
              label="Actions"
              {...otSelect("actions", options?.otActions)}
            />
          </div>

          {/* order_tracking's own RMA comment - read-only here; it is edited in
              Order Tracking Center, which owns that record. */}
          {String(record?.rmaComment || "").trim() ? (
            <div className="otc-comment-block">
              <div className="otc-comment-label">RMA Comment</div>
              <div className="otc-comment-body">{record.rmaComment}</div>
            </div>
          ) : null}
        </DetailSection>

        {/* ---------- Open Order (OOR) ---------- */}
        <DetailSection
          title="Open Order Detail (OOR)"
          subtitle={oor ? undefined : "no matching OOR record"}
        >
          {oor ? (
            <>
              <div className="otc-detail-grid">
                <SelectField label="Status" {...oorSelect("status")} />
                <SelectField label="Priority" {...oorSelect("priority")} />
                <SelectField
                  label="Expedite"
                  value={oor.expedite}
                  options={EXPEDITE_OPTIONS}
                  onSave={(next: string) =>
                    onSaveOor?.(record, "expedite", next)
                  }
                />
                <SelectField label="Stock" {...oorSelect("stock")} />
                <SelectField label="Resolution" {...oorSelect("resolution")} />
                <SelectField label="Sub-Status" {...oorSelect("action")} />
                <SelectField label="Send To" {...oorSelect("send_to")} />
                <SelectField label="Cal Type" {...oorSelect("cal_type_required")} />
                <DetailField label="SO Date" {...oorProps("po_date")} />
                <DetailField label="PO Sent" {...oorProps("po_sent")} />
                <DetailField label="Vendor Shipped" {...oorProps("vendor_shipped")} />
                <DetailField label="Wire Sent" {...oorProps("wire_sent")} />
                <DetailField label="Tracking #" {...oorProps("tracking_number")} />
                <DetailField label="Date Entered" {...oorProps("date_created")} />
                <DetailField
                  label="Last Update"
                  value={formatSnapshotDate(oor.last_update)}
                />
              </div>

              {/* The four comment families are the reason this section exists --
                  today they are only reachable by opening OOR separately. */}
              <div className="otc-comment-blocks">
                {visibleBlocks.map((block) => (
                  <CommentBlockCard
                    key={block.block}
                    block={block}
                    oor={oor}
                    manuOptions={options?.manu}
                    onSave={(field: string, next: string) =>
                      onSaveOor?.(record, field, next)
                    }
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="text-[12px] text-gray-500">
              No Open Order record matches quote {record?.quote_id || EM_DASH}.
            </div>
          )}
        </DetailSection>
      </div>
    </div>
  );
};

const OTCSnapshot = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchGenRef = useRef(0);
  const pendingOpenRef = useRef<{ id?: any; quoteId?: any } | null>(
    location?.state
      ? {
          id: (location.state as any)?.openRecordId,
          quoteId: (location.state as any)?.openQuoteId,
        }
      : null,
  );
  const [highlightRowKey, setHighlightRowKey] = useState<string | null>(null);
  const [paidStatusOptions, setPaidStatusOptions] = useState<StatusOption[]>(
    [],
  );
  const [leadFromOptions, setLeadFromOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState({ index: -1, value: "" });
  const [activeDueDateKey, setActiveDueDateKey] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const STATUS_TYPE_OTC = "otc"; // Type for OTC Snapshot paid statuses

  // ✅ current month/year
  const OTC_STATE_KEY = "otcState";
  const _otcSaved = (() => { try { return JSON.parse(localStorage.getItem(OTC_STATE_KEY) || "null") || {}; } catch { return {}; } })();
  const [sortRules, setSortRules] = useState<SortRule[]>(() => normalizeSortRules(_otcSaved.sortRules, OTC_SORT_KEYS));
  const buildSortParams = (rules = sortRules) => {
    const sort = sortRulesToQuery(rules, OTC_SORT_KEYS);
    return sort ? { sort } : {};
  };
  const [currentDate, setCurrentDate] = useState(_otcSaved.startDate ? dayjs(_otcSaved.startDate) : dayjs());
  const [startDate, setStartDate] = useState(_otcSaved.startDate ? dayjs(_otcSaved.startDate) : dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(_otcSaved.endDate ? dayjs(_otcSaved.endDate) : dayjs().endOf("month"));
  const [breakEvenPoint, setBreakEvenPoint] = useState(_otcSaved.breakEvenPoint || "");
  const [savedBreakEvenPoint, setSavedBreakEvenPoint] = useState("");
  const [oldestMonth, setOldestMonth] = useState<dayjs.Dayjs | null>(null);

  // Choice lists for the dropdown fields in the expanded row. Loaded once and
  // shared by every row, so opening ten orders does not mean ten fetches.
  const [detailOptions, setDetailOptions] = useState<Record<string, any[]>>({
    status: [],
    priority: [],
    stock: [],
    action: [],
    resolution: [],
    send_to: [],
    cal_type_required: [],
    manu: [],
    vendor: [],
    otActions: [],
  });

  // Which rows are expanded to show their full model-line list. Persisted so a
  // reload (or a trip out to a quote and back) keeps the rows you opened open.
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>(() =>
    Array.isArray(_otcSaved.expandedRowKeys) ? _otcSaved.expandedRowKeys : [],
  );

  // Pulls every dropdown's choices in parallel. Each list is independent, so a
  // failure in one leaves the rest usable rather than blanking all the menus.
  useEffect(() => {
    let cancelled = false;

    // These endpoints do not agree on a response shape: ship-to and cal-type
    // return { data: [...] }, while manufacturers and vendors may return a bare
    // array or a wrapper. Normalise before touching it — calling .map on the
    // wrapper object is what threw "(rows || []).map is not a function".
    const asRows = (res: any): any[] => {
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.data)) return res.data;
      if (Array.isArray(res?.rows)) return res.rows;
      return [];
    };

    const toOptions = (res: any, key: string) => {
      const seen = new Set<string>();
      return asRows(res)
        .map((row: any) => String(row?.[key] ?? "").trim())
        .filter((name: string) => {
          if (!name || seen.has(name)) return false;
          seen.add(name);
          return true;
        })
        .map((name: string) => ({ value: name, label: name }));
    };

    const load = async () => {
      const [statuses, shipTos, calTypes, manus, vendorList] = await Promise.all([
        getStatuses().catch(() => []),
        getShipTos().catch(() => []),
        getCalTypes().catch(() => []),
        getManufacturers().catch(() => []),
        getVendors().catch(() => []),
      ]);

      if (cancelled) return;

      const byType = (type: string) =>
        asRows(statuses)
          .filter((item: any) => item?.type === type)
          .map((item: any) => String(item?.statusName ?? "").trim())
          .filter(Boolean)
          .map((name: string) => ({ value: name, label: name }));

      setDetailOptions({
        status: byType(OOR_STATUS_TYPES.status),
        priority: byType(OOR_STATUS_TYPES.priority),
        stock: byType(OOR_STATUS_TYPES.stock),
        action: byType(OOR_STATUS_TYPES.action),
        resolution: byType(OOR_STATUS_TYPES.resolution),
        otActions: byType(STATUS_TYPE_OTC_ACTIONS),
        send_to: toOptions(shipTos, "name"),
        cal_type_required: toOptions(calTypes, "name"),
        manu: toOptions(manus, "name"),
        vendor: toOptions(vendorList, "name"),
      });
    };

    // A dropdown with no choices is a far smaller problem than a page that will
    // not render, so nothing in here is allowed to escape.
    load().catch((error) => {
      console.error("Failed to load detail dropdown options:", error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------- Status API Calls ----------------
  const refreshPaidStatuses = async () => {
    try {
      const statusResponse = await getStatuses();
      if (Array.isArray(statusResponse) && statusResponse.length > 0) {
        const mappedStatuses = statusResponse
          .filter((item) => item.type === STATUS_TYPE_OTC)
          .map((item) => ({
            id: item.id,
            value: item.statusName,
            label: item.statusName,
            type: item.type,
          }));
        setPaidStatusOptions(mappedStatuses);
      } else {
        setPaidStatusOptions([]);
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to load paid statuses",
      );
    }
  };

  const handleEditStatus = (index: number) => {
    setEditingStatus({ index, value: paidStatusOptions[index].label });
  };

  const handleSaveStatus = async () => {
    if (editingStatus.index < 0 || !editingStatus.value.trim()) return;
    const targetStatus = paidStatusOptions[editingStatus.index];
    if (!targetStatus?.id) return;

    try {
      await updateStatus(targetStatus.id, {
        statusName: editingStatus.value.trim(),
        type: STATUS_TYPE_OTC,
      });
      // message.success("Status updated successfully");
      setEditingStatus({ index: -1, value: "" });
      await refreshPaidStatuses();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to update status",
      );
    }
  };

  const handleAddStatus = async () => {
    if (!newStatus.trim()) return;
    try {
      await createStatus({
        statusName: newStatus.trim(),
        type: STATUS_TYPE_OTC,
      });
      // message.success("Status added successfully");
      setNewStatus("");
      await refreshPaidStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add status");
    }
  };

  const handleDeleteStatus = async (index: number) => {
    const targetStatus = paidStatusOptions[index];
    if (!targetStatus?.id) return;
    try {
      await deleteStatus(targetStatus.id);
      // message.success("Status deleted successfully");
      await refreshPaidStatuses();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to delete status",
      );
    }
  };

  const refreshLeadFromOptions = async () => {
    try {
      const response = await getLeadFromOptions();
      if (Array.isArray(response) && response.length > 0) {
        setLeadFromOptions(
          response.map((item: any) => ({
            value: item.name,
            label: item.name,
          })),
        );
      } else {
        setLeadFromOptions([]);
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to load lead from options",
      );
    }
  };

  const refreshMonthRange = async () => {
    try {
      const response = await getShippedOrderMonthRange();
      const oldest = response?.oldestDate ? dayjs(response.oldestDate).startOf("month") : null;
      setOldestMonth(oldest && oldest.isValid() ? oldest : dayjs().startOf("month"));
    } catch (error: any) {
      setOldestMonth(dayjs().startOf("month"));
      message.error(
        error?.response?.data?.message || "Failed to load month range",
      );
    }
  };

  // ---------------- API ----------------
  const fetchData = async (rulesOverride = sortRules) => {
    const gen = ++fetchGenRef.current;
    setDataSource([]);   // clear immediately so no old-month data persists during load
    try {
      setLoading(true);

      const res = await getShippedOrdersByDateRange(
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD"),
        buildSortParams(rulesOverride),
      );

      if (gen !== fetchGenRef.current) return; // stale response — a newer fetch is pending

      const rangeStart = startDate.format("YYYY-MM-DD");
      const rangeEnd   = endDate.format("YYYY-MM-DD");

      const formatted = res.data
        .map((item: any) => ({
          // Include the row id: date+customer+model alone can collide across two
          // real orders, which would make them expand and highlight as one row.
          key: [
            item.date ? dayjs(item.date).format("YYYY-MM-DD") : "",
            String(item.customer || "").trim(),
            String(item.model || "").trim(),
            String(item.id ?? ""),
          ].join("|"),
          id: item.id,
          quote_id: item.quote_id,
          rawDate: item.date ? dayjs(item.date).format("YYYY-MM-DD") : null,
          date: item.date ? dayjs(item.date).format("MM/DD/YYYY") : "",
          customer: item.customer || "",
          gm: formatOtcCurrencyValue(item.gross_margin),
          gs: formatOtcCurrencyValue(item.gross_sale),
          shipping: formatOtcCurrencyValue(item.shipping_cost),
          status: item.po_status || "",
          model: item.model || "",
          model2: item.model_2 || "",
          model3: item.model_3 || "",
          model4: item.model_4 || "",
          model5: item.model_5 || "",
          sn: item.serial_num1 || "",
          sn2: item.serial_num2 || "",
          sn3: item.serial_num3 || "",
          sn4: item.serial_num4 || "",
          sn5: item.serial_num5 || "",
          trackingNo: item.tracking_no || "",
          trackingNo2: item.tracking_no_2 || "",
          trackingNo3: item.tracking_no_3 || "",
          trackingNo4: item.tracking_no_4 || "",
          trackingNo5: item.tracking_no_5 || "",
          comment: item.otc_comments || "",
          poSent: item.vendor || "",
          paid: item.paid || "",
          dueDate: item.invoice_due_date || "",
          leadFrom: item.lead_from || "",
          salesOrder: item.sales_order || "",
          custPo: item.po_number || "",
          invoiceDate: item.invoice_date || "",
          ateInvoiceNo: item.ate_invoice_no || "",
          invoiceTotal:
            item.invoice_total !== undefined && item.invoice_total !== null
              ? String(item.invoice_total)
              : "",
          invoiceRequestedAt: item.invoice_requested_at || null,

          // ---- OOR detail, mirrored for the expanded row ----
          // `vendor` is already bound above as `poSent` (the "PO Sent?" column);
          // it is re-exposed as vendor1 so line 1 can show it without the two
          // fields sharing a name and cross-wiring on edit.
          manu: item.manu || "",
          manu2: item.manu2 || "",
          manu3: item.manu3 || "",
          manu4: item.manu4 || "",
          manu5: item.manu5 || "",
          qty: item.qty ?? "",
          qty2: item.qty_2 ?? "",
          qty3: item.qty_3 ?? "",
          qty4: item.qty_4 ?? "",
          qty5: item.qty_5 ?? "",
          vendor1: item.vendor || "",
          vendor2: item.vendor2 || "",
          vendor3: item.vendor3 || "",
          vendor4: item.vendor4 || "",
          vendor5: item.vendor5 || "",
          dateShipped: item.date_shipped || "",
          dateShipped2: item.date_shipped_2 || "",
          rental: item.rental,
          rentalPeriod: item.rental_period || "",
          rentalPeriod2: item.rental_period2 || "",
          rma: item.rma || "",
          rmaComment: item.rma_comment || "",
          vendorRma: item.vendor_rma || "",
          orderComplete: item.order_complete || "",
          vendorPaid: item.when || "",
          actions: item.actions || "",

          // Open Order (OOR) record joined on quote_id by the backend, or null
          // when this quote has no OOR row. Kept as a nested object so it is
          // always obvious which table a field came from.
          openOrder: item.openOrder || null,
        }))
        .filter((item: any) =>
          item.rawDate ? item.rawDate >= rangeStart && item.rawDate <= rangeEnd : true,
        );

      setDataSource(formatted);
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      console.error(err);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem(OTC_STATE_KEY, JSON.stringify({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      breakEvenPoint,
      sortRules,
      expandedRowKeys,
    }));
  }, [startDate, endDate, breakEvenPoint, sortRules, expandedRowKeys]);

  // Changing the month (or a sort) swaps the rows out from under us. Drop any
  // expanded key whose row is gone so the saved list cannot grow unbounded.
  useEffect(() => {
    if (expandedRowKeys.length === 0) return;
    const visible = new Set(dataSource.map((row: any) => row.key));
    const stillVisible = expandedRowKeys.filter((key) => visible.has(key));
    if (stillVisible.length !== expandedRowKeys.length) {
      setExpandedRowKeys(stillVisible);
    }
  }, [dataSource]);
  const handleShowAll = async () => {
    setSortRules([]);
    await fetchData([]);
  };

  const handleApplySortRules = async (rules: SortRule[]) => {
    const normalized = normalizeSortRules(rules, OTC_SORT_KEYS);
    setSortRules(normalized);
    await fetchData(normalized);
  };

  useEffect(() => {
    refreshPaidStatuses();
    refreshLeadFromOptions();
    refreshMonthRange();
  }, []);

  // Arrived from Order Tracking / Open Order via a "jump to quote id" link —
  // look up which month that record's date falls in, and switch the month
  // range so fetchData() below pulls it in. The actual row highlight/scroll
  // happens once dataSource has loaded (see the effect after fetchData's).
  useEffect(() => {
    const pending = pendingOpenRef.current;
    if (!pending) return;
    const quoteId = String(pending.quoteId || "").trim();
    if (!quoteId) {
      pendingOpenRef.current = null;
      navigate(location.pathname, { replace: true });
      return;
    }
    (async () => {
      try {
        const records = await getOrderTrackingByQuoteId(quoteId);
        const record = Array.isArray(records) ? records[0] : records;
        if (record?.date) {
          applyMonthRange(dayjs(record.date));
        } else {
          message.warning(
            `No shipped-order date found for Quote ID ${quoteId}.`,
          );
          pendingOpenRef.current = null;
        }
      } catch (error: any) {
        message.error(
          error?.response?.data?.message ||
            `Failed to look up Quote ID ${quoteId}`,
        );
        pendingOpenRef.current = null;
      } finally {
        navigate(location.pathname, { replace: true });
      }
    })();
  }, []);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  // Once the month's data has loaded, find the record the pending "jump to
  // quote id" link was aiming for, highlight it, and scroll it into view.
  useEffect(() => {
    const pending = pendingOpenRef.current;
    if (!pending || dataSource.length === 0) return;
    const { id, quoteId } = pending;
    const match = dataSource.find((r: any) => {
      if (id != null && r.id != null && String(r.id) === String(id))
        return true;
      if (quoteId && r.quote_id && String(r.quote_id) === String(quoteId))
        return true;
      return false;
    });
    pendingOpenRef.current = null;
    if (!match) {
      message.warning(
        `No Order Tracking record found for Quote ID ${quoteId} in ${dayjs(
          startDate,
        ).format("MMMM YYYY")}.`,
      );
      return;
    }
    setHighlightRowKey(match.key);
    window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-row-key="${match.key}"]`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    window.setTimeout(() => setHighlightRowKey(null), 3000);
  }, [dataSource]);

  // ---------------- Month Navigation ----------------
  const applyMonthRange = (date: any) => {
    if (!date) return;
    setCurrentDate(date);
    setStartDate(date.startOf("month"));
    setEndDate(date.endOf("month"));
  };

  const handlePrevMonth = () => {
    const next = currentDate.subtract(1, "month");
    applyMonthRange(next);
  };

  const handleNextMonth = () => {
    const next = currentDate.add(1, "month");
    applyMonthRange(next);
  };

  const latestMonth = dayjs().startOf("month");
  const firstMonth = oldestMonth && oldestMonth.isValid()
    ? oldestMonth
    : latestMonth;
  const monthRecordCount = Math.max(1, latestMonth.diff(firstMonth, "month") + 1);
  const monthRecordIndex = Math.max(
    0,
    Math.min(currentDate.startOf("month").diff(firstMonth, "month"), monthRecordCount - 1),
  );

  const handleMonthSliderCommit = (index: number) => {
    const safeIndex = Math.max(0, Math.min(index, monthRecordCount - 1));
    applyMonthRange(firstMonth.add(safeIndex, "month"));
  };

  const formatCurrency = (num: number) =>
    `$${Math.round(num).toLocaleString()}`;
  const formatCurrencyWhole = (num: number) =>
    `$${Math.round(num).toLocaleString()}`;

  const isTestUnlimited = (leadFrom: any) => {
    const v = String(leadFrom || "").trim().toLowerCase();
    return v === "test unlimited" || v === "testunl" || v === "testunlimited";
  };

  // total GM & GS
  const totalGM = dataSource.reduce(
    (sum, item) => sum + parseOtcCurrencyValue(item.gm),
    0,
  );

  const totalGS = dataSource.reduce(
    (sum, item) => sum + parseOtcCurrencyValue(item.gs),
    0,
  );

  const testUnlimitedGM = dataSource.reduce(
    (sum, item) =>
      isTestUnlimited(item.leadFrom)
        ? sum + parseOtcCurrencyValue(item.gm)
        : sum,
    0,
  );
  const breakEvenNumeric = Number.parseFloat(breakEvenPoint || "0") || 0;
  const currentPositionValue = totalGM - breakEvenNumeric;

  const fetchBreakEvenPoint = async (dateRef = currentDate) => {
    try {
      const month = dateRef.month() + 1;
      const year = dateRef.year();
      const response = await getOtcBreakEvenByMonth(month, year);
      const value = response?.breakEvenValue;
      const normalized =
        value === null || value === undefined
          ? "0"
          : String(Math.round(Number(value)));
      setBreakEvenPoint(normalized);
      setSavedBreakEvenPoint(normalized);
    } catch (error: any) {
      setBreakEvenPoint("0");
      setSavedBreakEvenPoint("0");
      message.error(
        error?.response?.data?.message || "Failed to load break even point",
      );
    }
  };

  useEffect(() => {
    fetchBreakEvenPoint(currentDate);
  }, [currentDate]);

  const handleBreakEvenChange = (value: string) => {
    const normalized = String(value || "").replace(/[^0-9]/g, "");
    setBreakEvenPoint(normalized);
  };

  const handleSaveBreakEvenPoint = async () => {
    const parsed = Number.parseFloat(breakEvenPoint || "0");
    if (!Number.isFinite(parsed)) {
      message.error("Please enter a valid break even value");
      return;
    }

    const month = currentDate.month() + 1;
    const year = currentDate.year();
    const rounded = Math.round(parsed);
    const normalized = String(rounded);

    if (normalized === savedBreakEvenPoint || breakEvenPoint === "") return;

    try {
      await upsertOtcBreakEvenByMonth(month, year, rounded);
      setSavedBreakEvenPoint(normalized);
      setBreakEvenPoint(normalized);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Failed to save break even point",
      );
    }
  };

  // Lead-wise GM totals
  const leadTotals = dataSource.reduce((acc, item) => {
    const raw = (item.leadFrom || "Other").trim();
    const lead = raw.toLowerCase() === "ebay" ? "EBay" : raw;
    const gm = parseOtcCurrencyValue(item.gm);
    acc[lead] = (acc[lead] || 0) + gm;
    return acc;
  }, {});

  const backendField = (field: string) => OT_FIELD_MAP[field];

  const handleUpdate = async (record, field, value) => {
    try {
      const target = backendField(field);
      if (!target) return;
      await updateOrderTracking(record.id, { [target]: value });
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------- Optimistic saves ----------------
  //
  // Both panels write the same way: patch local state synchronously, fire the
  // request in the background, and roll that one field back if it fails. No
  // spinner and no await before the UI moves, so the value never flickers
  // between old and new — the edit simply stays put unless the server rejects it.

  const patchRow = (rowKey: string, patch: Record<string, any>) => {
    setDataSource((prev: any[]) =>
      prev.map((row: any) => (row.key === rowKey ? { ...row, ...patch } : row)),
    );
  };

  const patchOor = (rowKey: string, patch: Record<string, any>) => {
    setDataSource((prev: any[]) =>
      prev.map((row: any) =>
        row.key === rowKey && row.openOrder
          ? { ...row, openOrder: { ...row.openOrder, ...patch } }
          : row,
      ),
    );
  };

  // order_tracking side of the expanded row.
  const saveOrderTrackingField = (
    record: any,
    field: string,
    value: any,
  ) => {
    const target = backendField(field);
    if (!target || record?.id == null) return;

    const previous = record[field];
    if (value === previous) return;

    patchRow(record.key, { [field]: value });

    updateOrderTracking(record.id, { [target]: value === "" ? null : value })
      .catch((error: any) => {
        patchRow(record.key, { [field]: previous });
        message.error(
          error?.response?.data?.message || "Failed to save — change reverted",
        );
      });
  };

  // open_order side of the expanded row.
  const saveOpenOrderField = (record: any, field: string, value: any) => {
    const oorId = record?.openOrder?.id;
    if (!oorId) {
      message.error("No Open Order record to save to.");
      return;
    }

    const previous = record.openOrder?.[field];
    if (value === previous) return;

    patchOor(record.key, { [field]: value });

    updateOpenOrderComments(oorId, { [field]: value })
      .catch((error: any) => {
        patchOor(record.key, { [field]: previous });
        message.error(
          error?.response?.data?.message || "Failed to save — change reverted",
        );
      });
  };

  const updateLocalCell = (rowKey: string, field: string, value: any) => {
    setDataSource((prev: any[]) =>
      prev.map((row: any) => (row.key === rowKey ? { ...row, [field]: value } : row)),
    );
  };

  const handleRequestInvoice = async (record: any) => {
    if (record.invoiceRequestedAt) {
      const sentDate = dayjs(record.invoiceRequestedAt).format("MM/DD/YYYY hh:mm A");
      const proceed = window.confirm(
        `An invoice was already requested for this order on ${sentDate}. Send another request anyway?`,
      );
      if (!proceed) return;
    }

    const subjectId = record.quote_id || record.salesOrder || record.customer || "Order";
    const subject = `Invoice Request - ${subjectId}`;
    const modelSlots = getModelSlots(record);
    const modelLines = modelSlots.map(
      (slot) =>
        `${slot.model}  |  S#: ${slot.sn || "N/A"}  |  Tracking #: ${slot.tracking || "N/A"}`,
    );
    const details = [
      "Hello Accounting,",
      "",
      "Please create/send an invoice for the order below:",
      "",
      `Quote ID: ${record.quote_id || "N/A"}`,
      `Customer: ${record.customer || "N/A"}`,
      `Customer PO: ${record.custPo || "N/A"}`,
      `PO Date: ${record.date || "N/A"}`,
      `PO Status: ${record.status || "N/A"}`,
      `Gross Sale: ${record.gs || "N/A"}`,
      `Shipping: ${record.shipping || "N/A"}`,
      "",
      "Model(s):",
      ...(modelLines.length > 0 ? modelLines : ["N/A"]),
      "",
      `Comments: ${record.comment || "N/A"}`,
    ].join("\n");

    window.location.href =
      `mailto:${encodeURIComponent("accounting@alliancetesteq.com")}` +
      `?cc=${encodeURIComponent("rkanna@alliancetesteq.com")}` +
      `&subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(details)}`;

    const sentAt = new Date().toISOString();
    updateLocalCell(record.key, "invoiceRequestedAt", sentAt);
    if (record.id != null) {
      try {
        await updateOrderTracking(record.id, { invoice_requested_at: sentAt });
      } catch (error) {
        console.error("Failed to record invoice request timestamp:", error);
      }
    }
  };

  // ---------------- TABLE ----------------
  const columns: ColumnsType<any> = [
    // Placement of the expand toggle. antd deprecated expandIconColumnIndex in
    // v5.7; putting EXPAND_COLUMN here is the supported way to pin it left of Date.
    Table.EXPAND_COLUMN,
    {
      title: "Date",
      dataIndex: "date",
      width: 75,
      render: (v) => (
        <div className="h-5 flex items-center px-1 text-[13px] font-bold">{v}</div>
      ),
    },
    {
      title: "Quote ID",
      dataIndex: "quote_id",
      width: 90,
      render: (v, record) => (
        <div className="relative group h-5 flex items-center px-1 text-[13px] font-bold">
          <span
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ maxWidth: v ? 60 : "100%" }}
          >
            {v}
          </span>
          <QuoteIdQuickNav
            quoteId={v}
            rightOffset={0}
            targets={[
              { path: "/open-order", label: "Open Order" },
              { path: "/Quotes", label: "Quote", useQueryParam: true },
            ]}
          />
        </div>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      width: 80,
      render: (v) => (
        <Tooltip title={v}>
          <div
            className="h-5 flex items-center px-1 text-[13px] font-bold"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {v}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "GM",
      dataIndex: "gm",
      width: 55,
      render: (v) => (
        <div className="h-5 flex items-center px-1 text-[13px] font-bold bg-[#8fe7eb]">
          {v}
        </div>
      ),
    },
    {
      title: "GS",
      dataIndex: "gs",
      width: 55,
      render: (v) => (
        <div className="h-5 flex items-center px-1 text-[13px] font-bold">{v}</div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 65,
      render: (v) => (
        <div className="h-5 flex items-center px-1 text-[13px] font-bold">{v}</div>
      ),
    },
    {
      title: "Model",
      dataIndex: "model",
      width: 95,
      render: (v) => (
        <div className="h-5 flex items-center px-1 text-[13px] font-bold">{v}</div>
      ),
    },
    {
      title: "Comments",
      dataIndex: "comment",
      width: 180,
      // Fixed height with an internal scrollbar rather than autoSize: a long
      // comment used to stretch its whole row and push the grid apart. Expand
      // the row to edit one comfortably.
      render: (v, record) => (
        <Input.TextArea
          value={v || ""}
          size="small"
          className={`${inputStyle} otc-comment-cell`}
          style={{ backgroundColor: v ? "#bfe8bf" : "transparent" }}
          onChange={(e) => updateLocalCell(record.key, "comment", e.target.value)}
          onBlur={() => handleUpdate(record, "comment", record.comment)}
        />
      ),
    },
    {
      title: "PO Sent?",
      dataIndex: "poSent",
      width: 105,
      render: (v, record) => (
        <Input
          value={v || ""}
          size="small"
          className={inputStyle}
          style={{ backgroundColor: "#bfeef1" }}
          onChange={(e) => updateLocalCell(record.key, "poSent", e.target.value)}
          onBlur={() => handleUpdate(record, "poSent", record.poSent)}
        />
      ),
    },
    {
      title: "Paid?",
      dataIndex: "paid",
      width: 75,
      render: (v, record) => (
        <Select
          value={v || undefined}
          className="w-full [&_.ant-select-selector]:!bg-[#f0ef95] [&_.ant-select-selector]:!border-[#A29F9F]"
          size="small"
          placeholder=""
          onChange={(value) => {
            updateLocalCell(record.key, "paid", value);
            handleUpdate(record, "paid", value);
          }}
          options={[
            ...paidStatusOptions.map((opt) => ({
              value: opt.value,
              label: opt.label,
            })),
            { value: "edit_status", label: "✎ Edit Status..." },
          ]}
          onSelect={(value) => {
            if (value === "edit_status") {
              setIsStatusModalOpen(true);
            }
          }}
        />
      ),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      width: 95,
      render: (v, record) =>
        activeDueDateKey === record.key ? (
          <DatePicker
            value={v ? dayjs(v) : null}
            format="MM/DD/YYYY"
            placeholder=""
            open
            autoFocus
            className={`${inputStyle} otc-due-date-picker !bg-[#e8c0ea]`}
            size="small"
            onChange={(date) => {
              const nextValue = date ? date.format("YYYY-MM-DD") : null;
              updateLocalCell(record.key, "dueDate", nextValue);
              handleUpdate(record, "dueDate", nextValue);
              setActiveDueDateKey(null);
            }}
            onOpenChange={(open) => { if (!open) setActiveDueDateKey(null); }}
          />
        ) : (
          <div
            className="h-5 flex items-center px-1 text-[13px] font-bold cursor-pointer"
            style={{ backgroundColor: v ? "#e8c0ea" : "transparent" }}
            onClick={() => setActiveDueDateKey(record.key)}
          >
            {v ? dayjs(v).format("MM/DD/YYYY") : ""}
          </div>
        ),
    },
    {
      title: "",
      dataIndex: "poGoto",
      width: 68,
      onCell: () => ({ className: "otc-po-cell" }),
      onHeaderCell: () => ({ className: "otc-po-cell" }),
      render: (_: any, record: any) => (
        <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
          <button
            type="button"
            onClick={() =>
              navigate("/order-tracking", {
                state: {
                  openRecordId: record.id,
                  openQuoteId: record.quote_id,
                },
              })
            }
            style={{
              height: 25,
              padding: "0 4px",
              fontSize: 14,
              fontWeight: 400,
              color: "#000",
              background: "#b8b8b8",
              border: "1px solid #5a5a5a",
              borderRadius: 6,
              boxShadow: "inset 1px 1px 0 #d8d8d8, inset -1px -1px 0 #555555",
              cursor: "pointer",
              lineHeight: "23px",
            }}
          >
            PO
          </button>
          <Tooltip
            title={
              record.invoiceRequestedAt
                ? `Invoice requested ${dayjs(record.invoiceRequestedAt).format("MM/DD/YYYY hh:mm A")}`
                : "Request Invoice"
            }
          >
            <button
              type="button"
              aria-label="Request Invoice"
              onClick={() => handleRequestInvoice(record)}
              style={{
                position: "relative",
                height: 25,
                width: 25,
                padding: 0,
                fontSize: 13,
                fontWeight: 400,
                color: "#000",
                background: record.invoiceRequestedAt ? "#c9e8c9" : "#ffcc66",
                border: record.invoiceRequestedAt
                  ? "1px solid #4a8f4a"
                  : "1px solid #b58a3d",
                borderRadius: 6,
                boxShadow: "inset 1px 1px 0 #ffe4a8, inset -1px -1px 0 #b58a3d",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MailOutlined />
              {record.invoiceRequestedAt && (
                <CheckOutlined
                  style={{
                    position: "absolute",
                    bottom: -3,
                    right: -3,
                    fontSize: 9,
                    color: "#0a7a2f",
                    background: "#fff",
                    borderRadius: "50%",
                  }}
                />
              )}
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Lead From",
      dataIndex: "leadFrom",
      width: 90,
      render: (v, record) => (
        <Select
          value={v}
          className="w-full [&_.ant-select-selector]:!bg-[#f1f1f1] [&_.ant-select-selector]:!border-[#A29F9F]"
          size="small"
          onChange={(value) => {
            updateLocalCell(record.key, "leadFrom", value);
            handleUpdate(record, "leadFrom", value);
          }}
          options={leadFromOptions}
        />
      ),
    },
  ];

  const SummaryBox = ({ label, value, highlight = false }: any) => {
    return (
      <div className="mb-1.5 flex flex-col items-center">
        <div className="text-[12px] font-medium text-gray-700 mb-0.5 text-center leading-tight">
          {label}
        </div>

        <div
          className="w-32 h-6 flex items-center justify-center border border-gray-400 text-[13px] font-semibold"
          style={{ background: value ? "#f9a8d4" : "#fff" }}
        >
          {value ? formatCurrency(value) : ""}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-50 min-h-screen overflow-auto">
      <CommonHeader
        className="mb-0"
        currentRecord={monthRecordIndex + 1}
        disableNext={monthRecordIndex >= monthRecordCount - 1}
        disablePrevious={monthRecordIndex <= 0}
        onNextRecord={handleNextMonth}
        onPreviousRecord={handlePrevMonth}
        onRecordSliderCommit={handleMonthSliderCommit}
        onShowAll={handleShowAll}
        recordCount={monthRecordCount}
        recordIndex={monthRecordIndex}
        recordInputLabel="Records"
        rightContent={<PageLogoutButton />}
        totalActiveCount={monthRecordCount}
        totalLabel={sortRules.length > 0 ? "Total (Sorted)" : "Total (Unsorted)"}
        totalRecordCount={monthRecordCount}
        totalValue={monthRecordCount}
        sortColumns={OTC_EXPORT_COLUMNS}
        sortTitle="Sort OTC Snapshot Records"
        sortRules={sortRules}
        onApplySort={handleApplySortRules}
        exportColumns={OTC_EXPORT_COLUMNS}
        getData={() => dataSource}
        onImport={async (data) => { await bulkImportOTC(data); }}
        exportFilename="otc-snapshot"
      />
      {/* -------- HEADER WITH BUTTONS -------- */}
      <div className="rounded-lg px-2 pt-2 mb-3">
        <div className="flex items-center">
          {/* Heading — original left position, takes up remaining left space */}
          <h1 className="flex-1 text-base sm:text-lg font-bold text-gray-800 whitespace-nowrap">
            Order Tracking Snapshot
          </h1>

          {/* NAV BUTTONS + CURRENT POSITION — centered in the middle column */}
          <div className="flex items-center gap-1">
            <Button
              onClick={() => navigate("/open-order")}
              className={`${navBtnBaseClass} !bg-[#0a4fbf] hover:!bg-[#0a47ad] !text-white`}
            >
              OOR
            </Button>

            <Button
              // onClick={() => navigate("/open-order")}
              className={`${navBtnBaseClass} !bg-[#d2d2d2] hover:!bg-[#c8c8c8] !text-black`}
            >
              EQR
            </Button>

            <Button
              onClick={() => navigate("/Quotes")}
              className={`${navBtnBaseClass} !bg-[#32e63b] hover:!bg-[#26d430] !text-black`}
            >
              Quotes
            </Button>

            <Button
              onClick={() => navigate("/order-tracking")}
              className={`${navBtnBaseClass} !bg-[#d2d2d2] hover:!bg-[#c8c8c8] !text-black`}
            >
              Order Tracking Center
            </Button>

            <Button
              onClick={() => navigate("/open-order")}
              className={`${navBtnBaseClass} !bg-[#f2ee66] hover:!bg-[#e8e35a] !text-black`}
            >
              Open Order Report
            </Button>

            <Button
              onClick={() => navigate("/inventory")}
              className={`${navBtnBaseClass} !bg-[#d2d2d2] hover:!bg-[#c8c8c8] !text-black`}
            >
              Inventory
            </Button>
            <div className="ml-2 flex items-center bg-[#f2f2f2] border border-gray-300">
              <div className="px-2 py-1 text-[19px] leading-none font-semibold text-black whitespace-nowrap">
                Current Position:
              </div>
              <div className="px-3 py-1 bg-pink-300 text-[38px] leading-none font-bold text-black">
                {formatCurrencyWhole(currentPositionValue)}
              </div>
            </div>
          </div>
          {/* Right spacer — mirrors the heading width so the buttons stay truly centered */}
          <div className="flex-1" />
        </div>
      </div>

      {/* -------- MAIN -------- */}
      <div className="flex gap-4 bg-white rounded-2xl shadow-sm p-3">
        {/* SIDEBAR */}
        <div className="w-56 flex-shrink-0">
          <div className="p-3 border-r bg-white flex flex-col h-full">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-2">
              <Button
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={handlePrevMonth}
              />
              <DatePicker
                picker="month"
                value={currentDate}
                format="MMMM YYYY"
                onChange={(date) => applyMonthRange(date)}
                allowClear={false}
                suffixIcon={null}
                bordered={false}
                className="otc-month-picker cursor-pointer [&_input]:!text-[16px] [&_input]:!font-bold [&_input]:!text-gray-800 [&_input]:!text-center"
              />
              <Button
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={handleNextMonth}
              />
            </div>

            {/* Sidebar content */}
            <div>
              {/* Break Even */}
              <div className="mb-1.5 text-[12px]">
                <div className="font-semibold mb-0.5">Break Even Point</div>
                <Input
                  size="small"
                  value={breakEvenPoint}
                  prefix="$"
                  className="w-full h-7 !text-sm font-bold"
                  onChange={(e) => handleBreakEvenChange(e.target.value)}
                  onBlur={handleSaveBreakEvenPoint}
                  onPressEnter={handleSaveBreakEvenPoint}
                  placeholder="Enter break even value"
                />
              </div>
              {/* Dates */}
              <div className="mb-1.5 text-[12px] space-y-1">
                <WinDatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => {
                    const normalized = date.startOf("day");
                    setStartDate(normalized);
                    if (normalized.isAfter(endDate)) {
                      setEndDate(normalized.endOf("day"));
                    }
                  }}
                />
                <WinDatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => {
                    const normalized = date.endOf("day");
                    setEndDate(normalized);
                    if (normalized.isBefore(startDate)) {
                      setStartDate(normalized.startOf("day"));
                    }
                  }}
                />
              </div>
              {/* Other sidebar content */}
              <div className="mb-2 text-center">
                <div className="text-[15px] font-semibold">Gross Margin</div>
                <div className="bg-cyan-200 border border-gray-400 w-40 h-9 inline-flex items-center justify-center font-bold text-[25px] leading-none">
                  {formatCurrency(totalGM)}
                </div>
              </div>
              <div className="mb-2 text-center">
                <div className="text-[15px] font-semibold">Gross Sales</div>
                <div className="bg-white border border-gray-400 w-40 h-9 inline-flex items-center justify-center font-bold text-[25px] leading-none">
                  {formatCurrency(totalGS)}
                </div>
              </div>
              <SummaryBox
                label="Adwords GM Total"
                value={leadTotals["Addwords"]}
                // highlight
              />
              <SummaryBox
                label="Already Customer GM Total"
                value={leadTotals["Already Customer"]}
                // highlight
              />
              <SummaryBox
                label="Dealer GM Total"
                value={leadTotals["Dealer"]}
              />
              <SummaryBox label="EBay GM Total" value={leadTotals["EBay"]} />
              <SummaryBox label="Test Unlimited Total" value={testUnlimitedGM} />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="flex-1">
          <div
            className="rounded-xl overflow-auto"
          >
            <Table
              className="otc-compact-table"
              columns={columns}
              dataSource={dataSource}
              loading={loading && dataSource.length > 0}
              pagination={false}
              size="small"
              rowKey="key"
              rowClassName={(record: any) =>
                record.key === highlightRowKey ? "otc-row-highlight" : ""
              }
              expandable={{
                // Position comes from Table.EXPAND_COLUMN in `columns`.
                columnWidth: 24,
                expandedRowKeys,
                onExpandedRowsChange: (keys: any) =>
                  setExpandedRowKeys(keys as string[]),
                // Every row expands. The rule used to be "more than one model
                // line", from when the panel showed nothing else; it now carries
                // the order-tracking detail, the OOR record and its comments, so
                // a single-line order has just as much behind it.
                expandedRowRender: (record: any) => (
                  <OtcExpandedRow
                    record={record}
                    options={detailOptions}
                    leadFromOptions={leadFromOptions}
                    onSaveOt={saveOrderTrackingField}
                    onSaveOor={saveOpenOrderField}
                  />
                ),
                expandIcon: ({ expanded, onExpand, record }: any) => (
                  <button
                    type="button"
                    aria-label={expanded ? "Collapse order" : "Expand order"}
                    aria-expanded={expanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    className={`otc-expand-btn${expanded ? " is-expanded" : ""}`}
                  >
                    {/* One caret that rotates 90deg on expand, so the arrow
                        turns instead of swapping glyphs. */}
                    <CaretRightOutlined className="otc-expand-caret" />
                  </button>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Status Modal for Paid? Field */}
      {isStatusModalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 backdrop-blur-sm bg-white/30"
            onClick={() => setIsStatusModalOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
            <div className="relative bg-white rounded-lg shadow-xl border border-gray-300 w-80 max-h-96 overflow-hidden">
              <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-[12px] font-semibold text-gray-700">
                  Edit Paid Status Options
                </h3>
                <Button
                  type="text"
                  size="small"
                  className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => setIsStatusModalOpen(false)}
                >
                  ✕
                </Button>
              </div>

              <div className="p-3 max-h-64 overflow-y-auto">
                <div className="space-y-2 mb-3">
                  {paidStatusOptions.map((option, index) => (
                    <div
                      key={option.id ?? index}
                      className="flex items-center gap-1 group"
                    >
                      {editingStatus.index === index ? (
                        <>
                          <Input
                            value={editingStatus.value}
                            onChange={(e) =>
                              setEditingStatus({
                                ...editingStatus,
                                value: e.target.value,
                              })
                            }
                            size="small"
                            className="h-6 text-[10px] flex-1"
                            autoFocus
                            onPressEnter={handleSaveStatus}
                          />
                          <Button
                            type="primary"
                            size="small"
                            className="h-5 w-5 p-0 flex items-center justify-center bg-green-600 hover:bg-green-700"
                            onClick={handleSaveStatus}
                          >
                            <span className="text-[10px]">✓</span>
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-5 w-5 p-0 flex items-center justify-center text-gray-500"
                            onClick={() =>
                              setEditingStatus({ index: -1, value: "" })
                            }
                          >
                            <span className="text-[10px]">✕</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="text-[10px] flex-1">
                            {option.label}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 flex items-center justify-center text-gray-400 hover:text-blue-600"
                              onClick={() => handleEditStatus(index)}
                            >
                              <EditOutlined className="text-[12px]" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 flex items-center justify-center text-gray-400 hover:text-red-600"
                              onClick={() => handleDeleteStatus(index)}
                            >
                              <DeleteOutlined className="text-[12px]" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-2">
                  <div className="text-[10px] font-medium text-gray-500 mb-1">
                    Add New Status
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      placeholder="Enter new status"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      size="small"
                      className="h-6 text-[10px] flex-1"
                      onPressEnter={handleAddStatus}
                    />
                    <Button
                      type="primary"
                      size="small"
                      className="h-6 px-2 text-[9px] bg-[#004aad]"
                      onClick={handleAddStatus}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OTCSnapshot;
